import { useRef, type ReactNode } from "react";
import {
  ArrowRight,
  ClipboardList,
  ExternalLink,
  Globe,
  Monitor,
  Play,
  Rocket,
  FileCheck2,
  GitBranch,
  Layers,
  ListChecks,
  Puzzle,
  Copy,
  Rocket as RocketIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  getAgentPromptExplanation,
  getHostLabel,
  getLaunchCommand,
  buildPlannerFromRequestCommand,
} from "@/lib/chat/cli-guidance";
import { useScrollSpotlight } from "../scroll-spotlight";
import type { HelpStepComponentProps } from "../help-flows";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What does Planner do?                                            */
/* -------------------------------------------------------------------------- */

export function WhatIsPlannerStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What is the Planner?">
        <p className="text-sm leading-7 text-muted-foreground">
          Remember the Epic Request you created? The Planner takes that approved idea and turns it
          into a <strong className="text-foreground/90">real plan</strong> — with features, tasks,
          an order of work, and clear acceptance criteria for each piece.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          Think of it like handing your pitch to a project manager who comes back with a detailed
          breakdown of exactly what needs to happen, step by step.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="What does it create?">
        <p className="text-sm leading-7 text-muted-foreground">
          The Planner builds three things for you automatically:
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={Layers}
            title="Features"
            description="Big chunks of work, like chapters in a book. Each one is a meaningful piece of the whole."
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={ListChecks}
            title="Tasks"
            description="Small, specific steps inside each feature. Clear enough that anyone (or any agent) can do them."
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={GitBranch}
            title="Dependencies"
            description={"Which things need to be done first, and which can happen at the same time. No guessing."}
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Why not just write a to-do list?">
        <p className="text-sm leading-7 text-muted-foreground">
          A to-do list tells you <em>what</em> to do. A plan tells you <em>what, in what order,
          with what criteria for done</em>. The Planner also structures everything so the
          Orchestrator agent can pick it up and start executing automatically.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileCheck2 className="h-4 w-4 text-blue-400" />
            <span>Approved request</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Puzzle className="h-4 w-4 text-emerald-400" />
            <span>Planner builds epic</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ClipboardList className="h-4 w-4 text-amber-400" />
            <span>Ready to execute</span>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Run the Planner                                                  */
/* -------------------------------------------------------------------------- */

export function RunPlannerStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title={`Open your ${getHostLabel(host)}`}>
        <p className="text-sm leading-7 text-muted-foreground">
          Same as before — go to your terminal and make sure {getHostLabel(host)} is running. If {"it's"} not
          open yet:
        </p>
        <CopyableTerminal className="mt-3" title="Terminal" copyText={getLaunchCommand(host)}>
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">{getLaunchCommand(host)}</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Copy the command from your Epic Request">
        <p className="text-sm leading-7 text-muted-foreground">
          Open your approved Epic Request on{" "}
          <strong className="text-foreground/90">spectree.toro.com</strong>. {"You'll"} see a{" "}
          <strong className="text-foreground/90">Ready to Convert</strong> section with a
          command you can copy. {"Here's"} what it looks like in the actual UI:
        </p>

        {/* SpecTree UI mockup — Ready to Convert card */}
        <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.08] bg-[#0c1117]">
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <RocketIcon className="h-4 w-4 text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-400">Ready to Convert</span>
            </div>
            <p className="text-sm text-muted-foreground">
              This request has been approved. Run the command below in {getHostLabel(host)} to create an epic from it.
            </p>
            <div className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-[#060a0f] px-4 py-3">
              <code className="text-sm text-foreground">
                {buildPlannerFromRequestCommand("ER-238", host)}
              </code>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/[0.08] bg-white/[0.05]">
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground/60">
              {getAgentPromptExplanation(host)}
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm leading-7 text-muted-foreground">
          Click the <strong className="text-foreground/90">copy button</strong> next to the
          command, then paste it into the {getHostLabel(host)} prompt and press{" "}
          <strong className="text-foreground/90">Enter</strong>.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Track progress — in SpecTree or in your terminal">
        <p className="text-sm leading-7 text-muted-foreground">
          Once you press Enter, the Planner starts working. It runs through a 5-stage
          pipeline: <strong className="text-foreground/90">Analyze → Decompose → Detail → Evaluate → Verify</strong>.
          This usually takes a minute or two.
        </p>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          You can track progress in two places:
        </p>
        <div className="mt-3 space-y-2">
          <div className="flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10"><Globe className="h-4 w-4 text-emerald-400" /></div>
            <div>
              <p className="text-sm font-semibold text-foreground">On spectree.toro.com</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Your Epic Request page shows a live pipeline tracker — each stage lights up
                as it completes with timing info and a quality score.
              </p>
              <a
                href="https://spectree.toro.com/requests"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
              >
                Open Epic Requests
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-white/[0.06] bg-white/[0.03] p-4">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]"><Monitor className="h-4 w-4 text-foreground/70" /></div>
            <div>
              <p className="text-sm font-semibold text-foreground">In your terminal</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {"You'll"} see the Planner working in real time — creating features, tasks,
                and structured descriptions as it goes.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Review the generated plan                                        */
/* -------------------------------------------------------------------------- */

export function ReviewPlanStep({ onNavigateToFlow, onAdvance }: HelpStepComponentProps) {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Open the epic in SpecTree">
        <p className="text-sm leading-7 text-muted-foreground">
          Once the Planner finishes, your Epic Request is converted into a full{" "}
          <span className="font-medium text-foreground">Epic</span> — complete with features and
          tasks. Click into it on spectree.toro.com and give it a quick look, like reviewing a first
          draft from a teammate.
        </p>
        <a
          href="https://spectree.toro.com/epics"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
        >
          Open Epics
          <ExternalLink className="h-4 w-4" />
        </a>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Check: does it cover everything?">
        <p className="text-sm leading-7 text-muted-foreground">
          Scan through the features. Ask yourself:
        </p>
        <div className="space-y-2">
          <ReviewCheckItem>Did it capture everything from your original request?</ReviewCheckItem>
          <ReviewCheckItem>Are there any big pieces missing?</ReviewCheckItem>
          <ReviewCheckItem>Is anything included that {"shouldn't"} be?</ReviewCheckItem>
        </div>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          {"Don't"} worry about perfect wording — just make sure the scope feels right. If
          something looks off, you can ask for changes right here in the CLI and SpecTree will
          update the epic for you.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title={"What's next?"}>
        <p className="text-sm leading-7 text-muted-foreground">
          {"You've"} planned your epic — {"it's"} broken down into features and tasks, ready to
          build. {"Here's"} where to go from here:
        </p>
        <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] to-primary/[0.03] p-6">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/15 text-primary">
              <Rocket className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {"What's next"}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">
                Time to execute your epic
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Your plan is ready. The next step is the{" "}
                <strong className="text-foreground/90">Orchestrator</strong> — it reads your epic,
                figures out the right order, and spawns sub-agents to implement each feature in
                parallel. You just kick it off and watch it work.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => onNavigateToFlow?.("execute")}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <Play className="h-4 w-4" />
                  Continue to Execute an Epic
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onAdvance?.()}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.08]"
                >
                  Back to Help Center
                </button>
              </div>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* ── Helper ── */

function ReviewCheckItem({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-400">?</span>
      <p className="text-sm leading-6 text-muted-foreground">{children}</p>
    </div>
  );
}
